import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "../../../lib/api";
import { useI18n } from "../../../lib/i18n";
import { toast } from "sonner";
import {
  UserPlus, Clock, Mail, X, Send, Ban, User as UserIcon, Shield, Trash2, RefreshCw,
  Phone, Package, TrendingUp, Trophy, Activity, ShieldCheck, Loader2, CheckCircle2,
  Plus, AlertCircle,
} from "lucide-react";

/**
 * Employees panel — Phase 2 (invite flow completion).
 *
 * The list now mixes ACTIVE employees with PENDING / EXPIRED invites
 * returned by `GET /platform/dashboard/business/{id}/employees`. Each
 * row is tagged with `kind` so we render the correct badges + actions.
 *
 * Toast behaviour is now truthful — we only claim "invitation sent"
 * when the backend reports `email_sent: true`. Otherwise we surface
 * a distinct notice explaining the actual state (e.g. SMTP not
 * configured yet) so the owner never assumes an email went out.
 */
export default function DashEmployees({ businessId }) {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  // Bulk invite state — a list of rows so the owner can queue as many
  // employees as needed in a single submission. Legacy single-invite
  // behaviour is preserved simply by opening the modal with 1 row.
  const emptyRow = () => ({ email: "", name: "", title_ar: "" });
  const [rows, setRows] = useState([emptyRow()]);
  const [sharedRole, setSharedRole] = useState("employee");
  const [submitting, setSubmitting] = useState(false);
  const [bulkResults, setBulkResults] = useState(null); // { total, sent, failed, duplicates, results: [] }
  const [detail, setDetail] = useState(null); // clicked row (active or invite)
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    apiClient.get(`/platform/dashboard/business/${businessId}/employees`)
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [businessId]);

  const openInvite = () => {
    setRows([emptyRow()]);
    setSharedRole("employee");
    setBulkResults(null);
    setShowInvite(true);
  };
  const closeInvite = () => {
    setShowInvite(false);
    setBulkResults(null);
  };
  const addRow = () => setRows((r) => [...r, emptyRow()]);
  const removeRow = (idx) =>
    setRows((r) => (r.length <= 1 ? r : r.filter((_, i) => i !== idx)));
  const updateRow = (idx, patch) =>
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

  // Per-row + cross-row validation. Kept purely local so submit is
  // fast even with 100+ rows. Errors are indexed by row so we can
  // paint each invalid input inline instead of a single flat toast.
  const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  const rowErrors = useMemo(() => {
    const errs = {};
    const seen = new Map(); // email → first index
    rows.forEach((row, i) => {
      const email = (row.email || "").trim().toLowerCase();
      if (!email) {
        errs[i] = lang === "ar" ? "البريد مطلوب" : "email required";
        return;
      }
      if (!emailRe.test(email)) {
        errs[i] = lang === "ar" ? "بريد غير صالح" : "invalid email";
        return;
      }
      if (seen.has(email)) {
        errs[i] = lang === "ar"
          ? `مكرر مع الصف ${seen.get(email) + 1}`
          : `duplicate of row ${seen.get(email) + 1}`;
        return;
      }
      seen.set(email, i);
    });
    return errs;
  }, [rows, lang]);
  const hasErrors = Object.keys(rowErrors).length > 0;

  const submitInvite = async (e) => {
    e.preventDefault();
    if (submitting) return; // guard duplicate submissions
    if (hasErrors) {
      toast.error(lang === "ar"
        ? "صحّح الأخطاء في الصفوف قبل الإرسال."
        : "Fix the row errors before sending.");
      return;
    }
    setSubmitting(true);
    setBulkResults(null);
    try {
      const payload = {
        items: rows.map((r) => ({
          email: r.email.trim().toLowerCase(),
          role_key: sharedRole,
          title_ar: (r.title_ar || "").trim() || null,
          name: (r.name || "").trim() || null,
        })),
      };
      const { data } = await apiClient.post(
        `/platform/businesses/${businessId}/employees/invite/bulk`,
        payload,
      );
      setBulkResults(data);
      // Summary toast — one line no matter how many rows were sent.
      const sent = data?.sent || 0;
      const failed = data?.failed || 0;
      const dups = data?.duplicates || 0;
      const total = data?.total || 0;
      // Rows that created an invite but the SMTP send didn't fire
      // (e.g. EMAIL_SMTP_ENABLED=0 in preview). The invite row itself
      // exists and can be resent from the list, so this is NOT a total
      // failure — surface it explicitly.
      const queued = Math.max(0, total - sent - failed - dups);
      if (total > 0 && failed === 0 && dups === 0 && queued === 0) {
        toast.success(lang === "ar"
          ? `تم إرسال ${sent} دعوة بنجاح`
          : `${sent} invitations sent`);
      } else if (sent + queued > 0) {
        toast.success(lang === "ar"
          ? `أُنشئت ${sent + queued} دعوة (${sent} أُرسلت بالبريد)، فشل ${failed}، مكرر ${dups}`
          : `${sent + queued} invites created (${sent} emailed) · ${failed} failed · ${dups} duplicate`);
      } else {
        toast.error(lang === "ar"
          ? `فشل إنشاء جميع الدعوات (${failed + dups})`
          : `No invitation was created (${failed + dups})`);
      }
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(
        (typeof d === "object" && d?.[lang]) ||
        (typeof d === "string" && d) ||
        (lang === "ar" ? "تعذّر إرسال الدفعة" : "Bulk send failed"),
      );
    } finally { setSubmitting(false); }
  };

  // Turn the raw backend `email_reason` code into an actionable
  // Arabic sentence so the toast never leaks internal tokens like
  // "disabled" / "not_configured" to the operator.
  const emailFailReason = (code) => {
    if (code === "disabled")
      return lang === "ar"
        ? "خدمة البريد غير مُفعّلة على الخادم — يجب ضبط EMAIL_SMTP_ENABLED=1 في إعدادات الإنتاج."
        : "Email service is not enabled on the server (set EMAIL_SMTP_ENABLED=1).";
    if (code === "not_configured")
      return lang === "ar"
        ? "بيانات SMTP ناقصة على الخادم — يجب حقن EMAIL_SMTP_PASSWORD (App Password) في الأسرار."
        : "SMTP is missing credentials (inject EMAIL_SMTP_PASSWORD in secrets).";
    return lang === "ar"
      ? `تعذّر إرسال البريد: ${code || "خطأ غير معروف"}`
      : `Email failed: ${code || "unknown error"}`;
  };

  const resend = async (invId) => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(
        `/platform/businesses/${businessId}/employees/invites/${invId}/resend`,
      );
      if (data?.email_sent) {
        toast.success(lang === "ar" ? "أُعيد إرسال الدعوة" : "Invitation re-sent");
      } else {
        toast.error(emailFailReason(data?.email_reason));
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const reinviteActive = async (empId) => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(
        `/platform/businesses/${businessId}/employees/${empId}/reinvite`,
      );
      if (data?.email_sent) {
        toast.success(lang === "ar" ? "أُعيد إرسال دعوة الانضمام" : "Join link re-sent");
      } else {
        toast.error(emailFailReason(data?.email_reason));
      }
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === "object" && d?.[lang]) || (typeof d === "string" && d) || "Failed");
    } finally { setBusy(false); }
  };

  const removeEmployee = async (empId) => {
    if (!window.confirm(lang === "ar"
      ? "حذف هذا الموظف من النشاط؟ لا يتم حذف حسابه الشخصي."
      : "Remove this employee? Their personal account is not deleted."
    )) return;
    setBusy(true);
    try {
      await apiClient.delete(`/platform/businesses/${businessId}/employees/${empId}`);
      toast.success(lang === "ar" ? "تم حذف الموظف" : "Employee removed");
      setDetail(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === "object" && d?.[lang]) || (typeof d === "string" && d) || "Failed");
    } finally { setBusy(false); }
  };

  const revoke = async (invId) => {
    if (!window.confirm(lang === "ar" ? "إلغاء الدعوة؟" : "Revoke this invitation?")) return;
    setBusy(true);
    try {
      await apiClient.post(
        `/platform/businesses/${businessId}/employees/invites/${invId}/revoke`,
      );
      toast.success(lang === "ar" ? "أُلغيت الدعوة" : "Invitation revoked");
      setDetail(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const roleLabel = (k) => ({
    owner: "مالك", co_owner: "مالك مشارك", branch_manager: "مدير فرع",
    employee: "موظف", accountant: "محاسب", viewer: "مطّلع",
  }[k] || k);

  const rowInitial = (r) => {
    const src = r.name || r.email || r.user_id || "?";
    return src.trim().charAt(0).toUpperCase();
  };

  return (
    <div className="space-y-5" data-testid="dash-employees">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-[#8a8a8a]">{items.length} {lang === "ar" ? "عضو" : "members"}</div>
        <button
          type="button"
          onClick={openInvite}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-bold shadow-sm"
          style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
          data-testid="btn-invite-employee"
        >
          <UserPlus className="w-4 h-4" /> {lang === "ar" ? "دعوة موظفين" : "Invite employees"}
        </button>
      </div>

      {loading ? (
        <div className="text-[#8a8a8a]">…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-white border border-dashed border-[#0a0a0a]/10 p-10 text-center text-[#8a8a8a]">
          {lang === "ar" ? "لم تُضِف موظفين بعد" : "No employees yet"}
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] overflow-hidden">
          {items.map((r) => {
            const isInvite = r.kind === "invite";
            const isExpired = isInvite && r.status === "expired";
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setDetail(r)}
                data-testid={`employee-row-${r.id}`}
                className="w-full text-right flex items-center gap-3 p-4 border-b last:border-b-0 border-[#0a0a0a]/[0.06] hover:bg-[#faf7ee] focus:bg-[#faf7ee] outline-none transition"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-[#f7eecd]"
                     style={{ background: isInvite
                       ? "linear-gradient(135deg,#8a8a8a,#525252)"
                       : "linear-gradient(135deg,#0a0a0a,#1f1614)" }}>
                  {rowInitial(r)}
                </div>
                <div className="flex-1 min-w-0 text-start">
                  <div className="text-sm font-semibold text-[#0a0a0a] truncate">
                    {r.name || r.title_ar || roleLabel(r.role_key)}
                  </div>
                  <div className="text-[11px] text-[#8a8a8a] truncate">{r.email || "—"}</div>
                </div>
                {isInvite ? (
                  <span className="text-[10px] font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1"
                        style={{
                          background: isExpired ? "rgba(220,38,38,0.10)" : "rgba(201,165,92,0.12)",
                          color: isExpired ? "#b91c1c" : "#8a6f2e",
                        }}>
                    <Clock className="w-3 h-3" />
                    {isExpired ? (lang === "ar" ? "منتهية" : "expired") : (lang === "ar" ? "دعوة معلّقة" : "pending")}
                  </span>
                ) : (
                  <span className="text-[10px] font-semibold px-2 py-1 rounded-full"
                        style={{ background: "rgba(15,124,58,0.10)", color: "#0f7c3a" }}>
                    {lang === "ar" ? "نشط" : "active"}
                  </span>
                )}
                <span className="text-[10px] font-semibold px-2 py-1 rounded-full"
                      style={{ background: "rgba(201,165,92,0.12)", color: "#8a6f2e" }}>
                  {roleLabel(r.role_key)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Bulk invite modal ─────────────────────────────────────── */}
      {showInvite && (
        <button type="button" aria-label="close" onClick={closeInvite} className="fixed inset-0 z-40 bg-black/40" />
      )}
      {showInvite && (
        <div className="fixed inset-x-4 top-8 sm:inset-x-auto sm:right-1/2 sm:translate-x-1/2 sm:max-w-2xl w-auto sm:w-[42rem] z-50 rounded-2xl bg-white shadow-2xl p-5 max-h-[88vh] overflow-y-auto"
             data-testid="bulk-invite-modal">
          {!bulkResults ? (
            <form onSubmit={submitInvite} className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold">{lang === "ar" ? "دعوة موظفين" : "Invite employees"}</h3>
                  <p className="text-[11px] text-[#8a8a8a] mt-1 leading-5">
                    {lang === "ar"
                      ? "أضف أي عدد من الصفوف وأرسل جميع الدعوات دفعة واحدة."
                      : "Add any number of rows and send all invitations in one go."}
                  </p>
                </div>
                <button type="button" onClick={closeInvite}
                        className="text-[#525252] shrink-0" data-testid="bulk-invite-close">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Shared role — applies to every row in this batch */}
              <div>
                <label className="text-[11px] font-semibold text-[#525252] uppercase tracking-wide">
                  {lang === "ar" ? "الدور لهذه الدفعة" : "Role for this batch"}
                </label>
                <select value={sharedRole} onChange={(e) => setSharedRole(e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm bg-white"
                        data-testid="bulk-invite-role">
                  <option value="employee">{lang === "ar" ? "موظف" : "Employee"}</option>
                  <option value="branch_manager">{lang === "ar" ? "مدير فرع" : "Branch manager"}</option>
                  <option value="co_owner">{lang === "ar" ? "مالك مشارك" : "Co-owner"}</option>
                  <option value="accountant">{lang === "ar" ? "محاسب" : "Accountant"}</option>
                </select>
              </div>

              {/* Row list */}
              <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1"
                   data-testid="bulk-invite-rows">
                {rows.map((row, idx) => {
                  const err = rowErrors[idx];
                  return (
                    <div key={idx}
                         className={`grid grid-cols-12 gap-2 items-start p-2 rounded-lg border ${err ? "border-red-300 bg-red-50/40" : "border-[#0a0a0a]/8 bg-[#faf7ee]/40"}`}
                         data-testid={`bulk-invite-row-${idx}`}>
                      <div className="col-span-12 sm:col-span-5">
                        <input type="email" required placeholder="employee@example.com"
                               value={row.email}
                               onChange={(e) => updateRow(idx, { email: e.target.value })}
                               className="w-full px-2.5 py-2 rounded-md border border-[#0a0a0a]/15 text-sm"
                               data-testid={`bulk-invite-email-${idx}`}
                               dir="ltr" />
                      </div>
                      <div className="col-span-6 sm:col-span-3">
                        <input type="text" placeholder={lang === "ar" ? "اسم الموظف" : "Employee name"}
                               value={row.name}
                               onChange={(e) => updateRow(idx, { name: e.target.value })}
                               className="w-full px-2.5 py-2 rounded-md border border-[#0a0a0a]/15 text-sm"
                               data-testid={`bulk-invite-name-${idx}`} />
                      </div>
                      <div className="col-span-6 sm:col-span-3">
                        <input type="text" placeholder={lang === "ar" ? "المسمى الوظيفي" : "Title"}
                               value={row.title_ar}
                               onChange={(e) => updateRow(idx, { title_ar: e.target.value })}
                               className="w-full px-2.5 py-2 rounded-md border border-[#0a0a0a]/15 text-sm"
                               data-testid={`bulk-invite-title-${idx}`} />
                      </div>
                      <div className="col-span-12 sm:col-span-1 flex sm:justify-end">
                        <button type="button" onClick={() => removeRow(idx)}
                                disabled={rows.length <= 1}
                                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[#8a8a8a] hover:text-red-700 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                aria-label={lang === "ar" ? "حذف الصف" : "Remove row"}
                                data-testid={`bulk-invite-remove-${idx}`}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      {err ? (
                        <div className="col-span-12 text-[11px] text-red-700 inline-flex items-center gap-1"
                             data-testid={`bulk-invite-error-${idx}`}>
                          <AlertCircle className="w-3 h-3" /> {err}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between">
                <button type="button" onClick={addRow}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold border border-[#c9a55c]/50 text-[#8a6f2e] hover:bg-[#faf7ee]"
                        data-testid="bulk-invite-add-row">
                  <Plus className="w-3.5 h-3.5" /> {lang === "ar" ? "إضافة صف" : "Add row"}
                </button>
                <div className="text-[11px] text-[#8a8a8a]">
                  {lang === "ar"
                    ? `${rows.length} صف${rows.length > 1 ? "" : ""}`
                    : `${rows.length} row${rows.length === 1 ? "" : "s"}`}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-[#0a0a0a]/[0.06]">
                <button type="button" onClick={closeInvite}
                        className="px-4 py-2 rounded-full text-sm text-[#525252]">
                  {lang === "ar" ? "إلغاء" : "Cancel"}
                </button>
                <button type="submit" disabled={submitting || hasErrors}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold disabled:opacity-50"
                        style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                        data-testid="bulk-invite-submit">
                  {submitting
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Send className="w-4 h-4" />}
                  {submitting
                    ? (lang === "ar" ? "جارٍ الإرسال…" : "Sending…")
                    : (lang === "ar" ? `إرسال الدعوات (${rows.length})` : `Send invitations (${rows.length})`)}
                </button>
              </div>
            </form>
          ) : (
            <BulkResultsView
              results={bulkResults}
              lang={lang}
              emailFailReason={emailFailReason}
              onDone={closeInvite}
              onSendMore={() => {
                setRows([emptyRow()]);
                setBulkResults(null);
              }}
            />
          )}
        </div>
      )}

      {/* ── Detail modal (active OR invite) ──────────────────────── */}
      {detail && (
        <EmployeeDetailModal
          businessId={businessId}
          detail={detail}
          busy={busy}
          lang={lang}
          onClose={() => setDetail(null)}
          onRevoke={() => revoke(detail.id)}
          onResend={() => resend(detail.id)}
          onRemove={() => removeEmployee(detail.id)}
          onReinvite={() => reinviteActive(detail.id)}
          roleLabel={roleLabel}
        />
      )}
    </div>
  );
}

function EmployeeDetailModal({
  businessId, detail, busy, lang, onClose,
  onRevoke, onResend, onRemove, onReinvite, roleLabel,
}) {
  const isInvite = detail.kind === "invite";
  const [perf, setPerf] = useState(null);
  const [perfErr, setPerfErr] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);

  useEffect(() => {
    if (isInvite) return; // invites have no perf card
    setPerf(null); setPerfErr(null); setPerfLoading(true);
    apiClient
      .get(`/platform/dashboard/business/${businessId}/employees/${detail.id}/performance`)
      .then(({ data }) => setPerf(data))
      .catch((err) => {
        const d = err?.response?.data?.detail;
        setPerfErr(
          (typeof d === "object" && d?.[lang]) || (typeof d === "string" && d)
          || (lang === "ar" ? "تعذّر تحميل الأداء." : "Could not load performance."),
        );
      })
      .finally(() => setPerfLoading(false));
  }, [businessId, detail.id, isInvite, lang]);

  return (
    <>
      <button type="button" aria-label="close" onClick={onClose}
              className="fixed inset-0 z-40 bg-black/40" />
      <div
        className="fixed inset-x-4 top-8 sm:inset-x-auto sm:right-1/2 sm:translate-x-1/2 sm:max-w-lg w-auto sm:w-[36rem] z-50 rounded-2xl bg-white shadow-2xl p-5 max-h-[88vh] overflow-y-auto"
        data-testid="employee-detail-modal">

        {/* ─── Header ─── */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-lg text-[#f7eecd] shrink-0"
                 style={{ background: isInvite
                   ? "linear-gradient(135deg,#8a8a8a,#525252)"
                   : "linear-gradient(135deg,#0a0a0a,#1f1614)" }}>
              {(detail.name || detail.email || "?").trim().charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-bold text-[#0a0a0a] truncate" data-testid="employee-detail-name">
                {detail.name || detail.title_ar || (isInvite
                  ? (lang === "ar" ? "دعوة موظف" : "Employee invitation")
                  : (lang === "ar" ? "تفاصيل الموظف" : "Employee details"))}
              </h3>
              {/* Composite business identity — the required display
                  format: "{name} — {title} — {business}". Renders only
                  for active members where we have the full triple. */}
              {!isInvite && detail.name && detail.title_ar && perf?.business?.name ? (
                <div className="text-[11px] text-[#8a6f2e] font-semibold mt-0.5 leading-5"
                     data-testid="employee-identity-line">
                  {detail.title_ar} — {perf.business.name}
                </div>
              ) : (
                <div className="text-[11px] text-[#8a8a8a] truncate mt-0.5">{detail.email || "—"}</div>
              )}
              <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(201,165,92,0.12)", color: "#8a6f2e" }}>
                  {roleLabel(detail.role_key)}
                </span>
                {!isInvite ? (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                        style={{ background: "rgba(15,124,58,0.10)", color: "#0f7c3a" }}>
                    <CheckCircle2 className="w-3 h-3" />
                    {lang === "ar" ? "نشط" : "active"}
                  </span>
                ) : (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                        style={{ background: detail.status === "expired" ? "rgba(220,38,38,0.10)" : "rgba(201,165,92,0.12)",
                                 color: detail.status === "expired" ? "#b91c1c" : "#8a6f2e" }}>
                    <Clock className="w-3 h-3" />
                    {detail.status === "expired"
                      ? (lang === "ar" ? "منتهية" : "expired")
                      : (lang === "ar" ? "معلّقة" : "pending")}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-[#525252] shrink-0"
                  data-testid="employee-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ─── Performance strip (active employees only) ─── */}
        {!isInvite && (
          <div className="grid grid-cols-4 gap-2 mb-4" data-testid="employee-perf-strip">
            <StatChip icon={<Package className="w-3.5 h-3.5" />}
                      label={lang === "ar" ? "الإعلانات" : "Listings"}
                      value={perfLoading ? "…" : (perf?.listings?.total ?? "—")}
                      hint={perf ? (lang === "ar" ? `${perf.listings.active} نشطة` : `${perf.listings.active} active`) : null} />
            <StatChip icon={<TrendingUp className="w-3.5 h-3.5" />}
                      label={lang === "ar" ? "المبيعات" : "Sales"}
                      value={perfLoading ? "…" : (perf?.sales?.deals_completed ?? "—")}
                      hint={perf && perf.sales.total_value_sar
                        ? formatSar(perf.sales.total_value_sar, lang)
                        : null} />
            <StatChip icon={<Activity className="w-3.5 h-3.5" />}
                      label={lang === "ar" ? "النشاط" : "Activity"}
                      value={perfLoading ? "…" : (perf ? `${perf.activity.score_pct}%` : "—")}
                      hint={perf && perf.activity.max_peer_score
                        ? (lang === "ar" ? "مقارنة بالأعلى" : "vs. top peer")
                        : (lang === "ar" ? "لا بيانات كافية" : "no baseline")} />
            <StatChip icon={<Trophy className="w-3.5 h-3.5" />}
                      label={lang === "ar" ? "الترتيب" : "Rank"}
                      value={perfLoading ? "…" : (perf?.rank?.position != null
                        ? `${perf.rank.position}/${perf.rank.total}`
                        : "—")}
                      hint={perf?.rank?.top_pct != null && perf.rank.total > 1
                        ? (lang === "ar" ? `ضمن أعلى ${perf.rank.top_pct}%` : `top ${perf.rank.top_pct}%`)
                        : (perf?.rank?.total === 1 ? (lang === "ar" ? "الموظف الوحيد" : "sole employee") : null)} />
          </div>
        )}

        {/* ─── Profile fields ─── */}
        <div className="space-y-2 text-sm">
          <Field icon={<UserIcon className="w-4 h-4" />} label={lang === "ar" ? "الاسم" : "Name"}
                 value={detail.name || (isInvite ? (lang === "ar" ? "لم يُسجّل بعد" : "not registered yet") : "—")} />
          {detail.phone ? (
            <Field icon={<Phone className="w-4 h-4" />} label={lang === "ar" ? "الجوال" : "Mobile"}
                   value={detail.phone} dir="ltr" />
          ) : null}
          <Field icon={<Shield className="w-4 h-4" />} label={lang === "ar" ? "الدور" : "Role"} value={roleLabel(detail.role_key)} />
          <Field icon={<Mail className="w-4 h-4" />} label={lang === "ar" ? "المسمى الوظيفي" : "Title"} value={detail.title_ar || "—"} />
          {detail.branch_id
            ? <Field icon={<Shield className="w-4 h-4" />} label={lang === "ar" ? "الفرع" : "Branch"} value={detail.branch_id} />
            : null}
          {isInvite ? (
            <>
              <Field icon={<Clock className="w-4 h-4" />} label={lang === "ar" ? "الحالة" : "Status"}
                     value={detail.status === "expired"
                       ? (lang === "ar" ? "منتهية الصلاحية" : "expired")
                       : (lang === "ar" ? "معلّقة" : "pending")} />
              <Field icon={<Clock className="w-4 h-4" />} label={lang === "ar" ? "تاريخ الإنشاء" : "Created"} value={fmtDate(detail.created_at)} />
              <Field icon={<Clock className="w-4 h-4" />} label={lang === "ar" ? "تنتهي في" : "Expires"} value={fmtDate(detail.expires_at)} />
              {detail.resend_count ? (
                <Field icon={<Send className="w-4 h-4" />} label={lang === "ar" ? "مرات إعادة الإرسال" : "Resends"} value={String(detail.resend_count)} />
              ) : null}
            </>
          ) : (
            <>
              <Field icon={<Clock className="w-4 h-4" />} label={lang === "ar" ? "الانضمام" : "Joined"} value={fmtDate(detail.joined_at)} />
              {perf?.employee?.verified ? (
                <Field icon={<ShieldCheck className="w-4 h-4" />}
                       label={lang === "ar" ? "التوثيق" : "Verified"}
                       value={lang === "ar" ? "حساب موثّق" : "verified account"} />
              ) : null}
              <Field icon={<UserIcon className="w-4 h-4" />}
                     label={lang === "ar" ? "معرّف المستخدم" : "User ID"}
                     value={detail.user_id || perf?.employee?.user_id || "—"}
                     mono />
            </>
          )}
        </div>

        {/* ─── Performance detail block (active only) ─── */}
        {!isInvite && (
          <div className="mt-4 rounded-xl bg-[#faf7ee] border border-[#c9a55c]/25 p-3"
               data-testid="employee-perf-block">
            <div className="flex items-center gap-1.5 text-[11px] font-bold tracking-widest uppercase text-[#8a6f2e] mb-2">
              <Activity className="w-3.5 h-3.5" />
              {lang === "ar" ? "الأداء" : "Performance"}
            </div>
            {perfLoading ? (
              <div className="py-4 flex items-center justify-center text-[#8a8a8a]">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : perfErr ? (
              <div className="text-xs text-red-800">{perfErr}</div>
            ) : perf ? (
              <>
                <PerfRow label={lang === "ar" ? "إجمالي الإعلانات" : "Total listings"}
                         value={perf.listings.total} />
                <PerfRow label={lang === "ar" ? "الإعلانات النشطة" : "Active listings"}
                         value={perf.listings.active} />
                <PerfRow label={lang === "ar" ? "الصفقات المكتملة" : "Completed deals"}
                         value={perf.sales.deals_completed} />
                <PerfRow label={lang === "ar" ? "قيمة المبيعات" : "Sales value"}
                         value={formatSar(perf.sales.total_value_sar, lang)} />
                <div className="mt-2 pt-2 border-t border-[#c9a55c]/20">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-[#8a8a8a]">
                      {lang === "ar" ? "مؤشر النشاط" : "Activity index"}
                    </span>
                    <span className="font-bold text-[#0a0a0a]">{perf.activity.score_pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#0a0a0a]/[0.06] overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                         style={{
                           width: `${Math.max(2, perf.activity.score_pct)}%`,
                           background: "linear-gradient(90deg,#e8c873,#c9a55c)",
                         }} />
                  </div>
                  <div className="text-[10px] text-[#8a8a8a] mt-1.5 leading-5">
                    {lang === "ar"
                      ? `النسبة تُحسب بمعادلة (الإعلانات + المبيعات × ٣) مقابل أعلى موظف في نفس النشاط.`
                      : `Formula: (listings + sales × 3) vs. the top peer in this business.`}
                  </div>
                  {perf.rank?.total > 1 && perf.rank.position != null ? (
                    <div className="text-[11px] mt-2 flex items-center gap-1.5 text-[#0a0a0a]">
                      <Trophy className="w-3.5 h-3.5 text-[#8a6f2e]" />
                      {lang === "ar"
                        ? `ترتيبه بين الموظفين: ${perf.rank.position} من ${perf.rank.total}`
                        : `Rank among peers: ${perf.rank.position} of ${perf.rank.total}`}
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* ─── Action row ─── */}
        {isInvite && detail.status === "pending" && (
          <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-[#0a0a0a]/[0.06]">
            <button type="button" onClick={onRevoke} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold border border-red-300 text-red-800 hover:bg-red-50 disabled:opacity-50"
                    data-testid="invite-revoke">
              <Ban className="w-4 h-4" /> {lang === "ar" ? "إلغاء الدعوة" : "Revoke"}
            </button>
            <button type="button" onClick={onResend} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold disabled:opacity-50"
                    style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                    data-testid="invite-resend">
              <Send className="w-4 h-4" /> {lang === "ar" ? "إعادة الإرسال" : "Resend"}
            </button>
          </div>
        )}
        {isInvite && detail.status === "expired" && (
          <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-[#0a0a0a]/[0.06]">
            <button type="button" onClick={onResend} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold disabled:opacity-50"
                    style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                    data-testid="invite-resend-expired">
              <RefreshCw className="w-4 h-4" /> {lang === "ar" ? "إرسال دعوة جديدة" : "Send new invite"}
            </button>
          </div>
        )}
        {!isInvite && (
          <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-[#0a0a0a]/[0.06]">
            {detail.role_key !== "owner" && (
              <button type="button" onClick={onRemove} disabled={busy}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold border border-red-300 text-red-800 hover:bg-red-50 disabled:opacity-50"
                      data-testid="employee-remove">
                <Trash2 className="w-4 h-4" /> {lang === "ar" ? "حذف الموظف" : "Remove"}
              </button>
            )}
            {/* Re-invite (إعادة الدعوة) — always visible for active
                employees so it lives permanently next to «حذف الموظف».
                If the row has no linked email the backend returns 400
                with a clear Arabic reason, so we do not double-gate on
                the frontend anymore. */}
            <button type="button" onClick={onReinvite} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold disabled:opacity-50"
                    style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                    data-testid="employee-reinvite">
              <Send className="w-4 h-4" /> {lang === "ar" ? "إعادة الدعوة" : "Re-invite"}
            </button>          </div>
        )}
      </div>
    </>
  );
}

function StatChip({ icon, label, value, hint }) {
  return (
    <div className="rounded-xl bg-[#faf7ee] border border-[#c9a55c]/25 px-2 py-2 text-center">
      <div className="flex items-center justify-center gap-1 text-[#8a6f2e]">
        {icon}
        <span className="text-[9px] font-bold uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-base font-bold text-[#0a0a0a] mt-0.5">{value}</div>
      {hint ? <div className="text-[9px] text-[#8a8a8a] mt-0.5 truncate">{hint}</div> : null}
    </div>
  );
}

function PerfRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-xs py-1">
      <span className="text-[#525252]">{label}</span>
      <span className="font-bold text-[#0a0a0a]">{value}</span>
    </div>
  );
}

function formatSar(n, lang) {
  if (n == null) return "—";
  try {
    const fmt = new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US",
      { maximumFractionDigits: 0 });
    return `${fmt.format(n)} ${lang === "ar" ? "ر.س" : "SAR"}`;
  } catch { return `${n} SAR`; }
}

function Field({ icon, label, value, dir, mono }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-[#8a6f2e] mt-0.5">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wide text-[#8a8a8a]">{label}</div>
        <div className={`text-sm font-semibold text-[#0a0a0a] break-all ${mono ? "font-mono text-[11px]" : ""}`}
             dir={dir}>
          {value ?? "—"}
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ar-SA", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return String(iso).slice(0, 16); }
}

// Per-row outcome view rendered right inside the bulk-invite modal
// after submission. Keeps the owner in-context so they can immediately
// spot which rows failed, re-copy an address, or open a fresh batch.
function BulkResultsView({ results, lang, emailFailReason, onDone, onSendMore }) {
  const total = results?.total ?? 0;
  const sent = results?.sent ?? 0;
  const failed = results?.failed ?? 0;
  const dups = results?.duplicates ?? 0;
  const queued = Math.max(0, total - sent - failed - dups);
  const rows = results?.results || [];
  const errText = (r) => {
    if (r.error && typeof r.error === "object") return r.error[lang] || r.error.ar || r.error.en;
    if (typeof r.error === "string") return r.error;
    if (r.email_reason) return emailFailReason(r.email_reason);
    return lang === "ar" ? "فشل غير معروف" : "unknown failure";
  };
  return (
    <div className="space-y-4" data-testid="bulk-invite-results">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-bold">
            {lang === "ar" ? "نتيجة إرسال الدعوات" : "Bulk invite results"}
          </h3>
          <p className="text-[11px] text-[#8a8a8a] mt-1">
            {lang === "ar"
              ? `${total} صف · ${sent} أُرسلت بالبريد${queued ? ` · ${queued} أُنشئت بدون إرسال` : ""} · ${failed} فشل · ${dups} مكرر`
              : `${total} rows · ${sent} emailed${queued ? ` · ${queued} created (no email)` : ""} · ${failed} failed · ${dups} duplicate`}
          </p>
        </div>
        <button type="button" onClick={onDone}
                className="text-[#525252] shrink-0" data-testid="bulk-results-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="rounded-xl border border-[#0a0a0a]/[0.08] overflow-hidden max-h-[55vh] overflow-y-auto">
        {rows.map((r, i) => {
          const ok = r.status === "sent" || r.status === "queued";
          return (
            <div key={i}
                 className={`flex items-center gap-2 px-3 py-2 text-sm border-b last:border-b-0 border-[#0a0a0a]/[0.05] ${ok ? "bg-emerald-50/40" : (r.status === "duplicate_in_request" ? "bg-amber-50/40" : "bg-red-50/40")}`}
                 data-testid={`bulk-result-row-${i}`}>
              <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: ok ? "rgba(15,124,58,0.15)" : (r.status === "duplicate_in_request" ? "rgba(201,165,92,0.15)" : "rgba(220,38,38,0.15)"),
                             color: ok ? "#0f7c3a" : (r.status === "duplicate_in_request" ? "#8a6f2e" : "#b91c1c") }}>
                {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : (r.status === "duplicate_in_request" ? <AlertCircle className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />)}
              </span>
              <span className="flex-1 min-w-0 truncate font-mono text-[12px]" dir="ltr">{r.email || "—"}</span>
              <span className="text-[11px] font-semibold shrink-0"
                    style={{ color: ok ? "#0f7c3a" : (r.status === "duplicate_in_request" ? "#8a6f2e" : "#b91c1c") }}>
                {ok
                  ? (r.email_sent
                      ? (lang === "ar" ? "أُرسلت" : "sent")
                      : (lang === "ar" ? "أُنشئت" : "queued"))
                  : (r.status === "duplicate_in_request"
                      ? (lang === "ar" ? "مكرر" : "duplicate")
                      : (lang === "ar" ? "فشل" : "failed"))}
              </span>
              {!ok && (
                <span className="text-[11px] text-[#525252] truncate max-w-[45%] hidden sm:inline"
                      title={errText(r)}>
                  {errText(r)}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-[#0a0a0a]/[0.06]">
        <button type="button" onClick={onSendMore}
                className="px-4 py-2 rounded-full text-sm font-semibold border border-[#c9a55c]/50 text-[#8a6f2e] hover:bg-[#faf7ee]"
                data-testid="bulk-results-send-more">
          {lang === "ar" ? "دفعة جديدة" : "New batch"}
        </button>
        <button type="button" onClick={onDone}
                className="px-4 py-2 rounded-full text-sm font-bold"
                style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                data-testid="bulk-results-done">
          {lang === "ar" ? "تم" : "Done"}
        </button>
      </div>
    </div>
  );
}
